import React, { useEffect, useState } from "react";
import {
 Check, ArrowRight, Star, Music, Camera, Sparkles, Anchor,
 Wine, ChefHat, Clock, ChevronDown
} from "lucide-react";
import { EVENT_PACKAGES, ENTERTAINMENT_ADDONS } from "../data/eventPackages";

const API = process.env.REACT_APP_BACKEND_URL;

// Vibrant floating-venue palette, softer near-white base so the rose/pink/blue accent palettes pop
const CREAM = "#FBFCFD";
const CHAMPAGNE = "#FFC850";
const INK = "#0F1B3D";
const INK_SOFT = "rgba(15,27,61,0.72)";

// Per-cruise-type accent overrides
const ACCENT_THEMES = {
 rose: { main: "#E63E5E", soft: "#FFE9E2", second: "#FFC850", deep: "#7A1132", grad: "linear-gradient(120deg, #FFC850 0%, #FF4B8B 100%)" },
 fuchsia: { main: "#C026D3", soft: "#FAE8FF", second: "#FFC850", deep: "#5B1267", grad: "linear-gradient(120deg, #FFC850 0%, #C026D3 100%)" },
 blue: { main: "#2563EB", soft: "#E0EBFF", second: "#FFC850", deep: "#0B2371", grad: "linear-gradient(120deg, #FFC850 0%, #3B82F6 100%)" },
 emerald: { main: "#059669", soft: "#D1FAE5", second: "#FFC850", deep: "#053825", grad: "linear-gradient(120deg, #FFC850 0%, #10B981 100%)" },
 amber: { main: "#D97706", soft: "#FEF3C7", second: "#E63E5E", deep: "#4A2305", grad: "linear-gradient(120deg, #FFC850 0%, #E63E5E 100%)" },
};

// Explicit Tailwind grid-cols mapping (dynamic class names don't work with JIT)
const GRID_COLS = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3" };

const ICON_MAP = { Music, Camera, Sparkles, Anchor, Star };


export default function EventPackagesSection({ cruiseType = "wedding" }) {
 const staticData = EVENT_PACKAGES[cruiseType];
 const [data, setData] = useState(staticData);
 const [entertainment, setEntertainment] = useState(ENTERTAINMENT_ADDONS);
 const theme = ACCENT_THEMES[data?.accent] || ACCENT_THEMES.rose;
 const [activePkg, setActivePkg] = useState(0);
 const [openMenus, setOpenMenus] = useState(new Set());

 // Pull live CMS data , admin edits apply instantly. Falls back to static on failure.
 useEffect(() => {
  let cancelled = false;
  (async () => {
   try {
    const [pkgRes, entRes] = await Promise.all([
     fetch(`${API}/api/cms/packages/${cruiseType}`).then((r) => r.ok ? r.json() : null).catch(() => null),
     fetch(`${API}/api/cms/entertainment`).then((r) => r.ok ? r.json() : null).catch(() => null),
    ]);
    if (cancelled) return;
    if (pkgRes && pkgRes.packages) setData(pkgRes);
    if (entRes && Array.isArray(entRes.items)) setEntertainment(entRes.items);
   } catch { /* keep static fallback */ }
  })();
  return () => { cancelled = true; };
 }, [cruiseType]);

 const toggleMenu = (i) => {
  setOpenMenus((prev) => {
   const next = new Set(prev);
   if (next.has(i)) next.delete(i); else next.add(i);
   return next;
  });
 };

 if (!data) return null;
 const pkg = data.packages[activePkg] || data.packages[0];
 const colsClass = GRID_COLS[data.packages.length] || "md:grid-cols-2";

 return (
 <div data-testid={`event-packages-${cruiseType}`} style={{ backgroundColor: CREAM, color: INK }}>

  {/* ───── PACKAGE TIERS, full-width editorial intro, no header photo ───── */}
  <section className="relative pt-8 pb-14 lg:pt-12 lg:pb-20" style={{ backgroundColor: CREAM }}>
   <div className="absolute inset-0 pointer-events-none opacity-50" aria-hidden="true, ">
    <div className="absolute -top-20 right-0 w-[40rem] h-[40rem] rounded-full blur-3xl" style={{ backgroundColor: theme.main, opacity: 0.08 }} />
    <div className="absolute -bottom-20 left-0 w-[36rem] h-[36rem] rounded-full blur-3xl" style={{ backgroundColor: theme.second, opacity: 0.10 }} />
   </div>
   <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
    {/* Editorial intro band, left-aligned, generous spacing, NO photo */}
    <div className="max-w-3xl mb-14 lg:mb-20">
     <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-4 flex items-center gap-3" style={{ color: theme.main }}>
      <span className="w-8 h-px" style={{ backgroundColor: theme.main }} /> Package pricing · real menus
     </div>
     <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(2.25rem, 5.5vw, 4.5rem)", letterSpacing: "-0.025em", color: INK }}>
      {data.title.split(" ")[0]}{" "}
      <span className="italic font-light" style={{ color: theme.main }}>{data.title.split(" ").slice(1).join(" ").toLowerCase()}.</span>
     </h2>
     <p className="text-base lg:text-xl leading-[1.7] font-light mt-6 max-w-2xl" style={{ color: INK_SOFT }}>{data.intro}</p>

     {/* Trust strip */}
     <div className="flex flex-wrap gap-4 lg:gap-6 mt-8 text-xs lg:text-sm font-bold" style={{ color: INK }}>
      <span className="inline-flex items-center gap-2"><Anchor className="w-4 h-4" style={{ color: theme.main }} /> King St Wharf</span>
      <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" style={{ color: theme.main }} /> 3 to 4 hour charters</span>
      <span className="inline-flex items-center gap-2"><Star className="w-4 h-4" style={{ color: theme.main }} fill="currentColor" strokeWidth={0} /> 5★ Google · 2,500+ reviews</span>
     </div>
    </div>

    {/* PACKAGE TIER CARDS, taller, breathing, no skinny columns */}
    <div className={`grid ${colsClass} gap-6 lg:gap-7`}>
     {data.packages.map((p, i) => (
      <div key={i} className="relative rounded-3xl bg-white p-8 lg:p-10 overflow-hidden shadow-[0_30px_70px_-25px_rgba(15,27,61,0.22)] hover:-translate-y-1.5 transition-all duration-500" data-testid={`pkg-card-${i}`}>
       <div className="absolute top-0 left-0 right-0 h-2" style={{ backgroundColor: theme.main }} />
       <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full blur-3xl opacity-30" style={{ background: theme.main }} />

       {p.featured && (
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.22em] font-black mb-5" style={{ backgroundColor: CHAMPAGNE, color: INK }}>
         <Star className="w-3 h-3" fill="currentColor" strokeWidth={0} /> Most Popular
        </div>
       )}

       <div className="text-[10px] uppercase tracking-[0.32em] font-black mb-3" style={{ color: theme.main }}>Package {String(i + 1).padStart(2, "0")}</div>
       <h3 className="font-black text-2xl lg:text-3xl tracking-tight leading-tight mb-4" style={{ color: INK }}>{p.name}</h3>
       <div className="flex items-baseline gap-2 mb-2">
        <div className="font-black tracking-tight" style={{ fontSize: "clamp(2.5rem, 4.5vw, 3.75rem)", color: INK, letterSpacing: "-0.035em" }}>{p.price}</div>
        {p.priceNote && <div className="text-xs uppercase tracking-[0.22em] font-bold" style={{ color: INK_SOFT }}>{p.priceNote}</div>}
       </div>
       <div className="text-sm font-bold mb-2" style={{ color: theme.main }}>{p.sub}</div>
       {p.extras && <div className="text-xs italic mb-6" style={{ color: INK_SOFT }}>{p.extras}</div>}

       <div className="border-t pt-6 mb-6" style={{ borderColor: "rgba(15,27,61,0.08)" }}>
        <div className="text-[10px] uppercase tracking-[0.22em] font-black mb-4" style={{ color: INK_SOFT }}>Package includes</div>
        <ul className="space-y-3">
         {p.inclusions.map((inc, idx) => (
          <li key={idx} className="flex items-start gap-3 text-[15px] leading-snug" style={{ color: INK }}>
           <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: theme.main }} strokeWidth={2.5} /> <span>{inc}</span>
          </li>
         ))}
        </ul>
       </div>

       {p.rules && (
        <div className="border-t pt-6 mb-6" style={{ borderColor: "rgba(15,27,61,0.08)" }}>
         <div className="text-[10px] uppercase tracking-[0.22em] font-black mb-3" style={{ color: INK_SOFT }}>Important notes</div>
         <ul className="space-y-2 text-xs leading-snug" style={{ color: INK_SOFT }}>
          {p.rules.map((r, idx) => (
           <li key={idx} className="flex items-start gap-2">
            <span className="block w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: theme.main }} />
            <span>{r}</span>
           </li>
          ))}
         </ul>
        </div>
       )}

       <button onClick={() => { setActivePkg(i); toggleMenu(i); if (!openMenus.has(i)) setTimeout(() => { const el = document.querySelector(`[data-testid="menu-toggle-${i}"]`); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 80); }} className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-full font-black text-xs uppercase tracking-[0.18em] transition-all" style={{ background: openMenus.has(i) ? theme.grad : "rgba(15,27,61,0.06)", color: openMenus.has(i) ? "#fff" : INK }} data-testid={`pkg-view-menu-${i}`}>
        {openMenus.has(i) ? "✓ Menu open · click to close" : "View menu"} <ArrowRight className="w-3.5 h-3.5" />
       </button>
      </div>
     ))}
    </div>
   </div>
  </section>

 {/* PER-PACKAGE MENU ACCORDIONS, each package has its OWN clearly labelled accordion */}
 {data.packages.some((p) => p.menu) && (
 <section className="relative py-10 lg:py-14" style={{ backgroundColor: theme.soft }}>
  <div className="absolute inset-0 pointer-events-none opacity-50">
   <div className="absolute top-1/3 left-1/4 w-[28rem] h-[28rem] rounded-full blur-3xl" style={{ backgroundColor: theme.main, opacity: 0.08 }} />
  </div>
  <div className="relative max-w-5xl mx-auto px-5 lg:px-10">
   <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-5 flex items-center gap-3" style={{ color: theme.main }}>
    <ChefHat className="w-4 h-4" />
    <span className="w-8 h-px" style={{ backgroundColor: theme.main }} /> Package menus
   </div>
   <div className="space-y-3">
    {data.packages.map((p, pIdx) => {
     if (!p.menu) return null;
     const isOpen = openMenus.has(pIdx);
     return (
      <div key={pIdx} className="rounded-3xl bg-white shadow-[0_20px_50px_-25px_rgba(15,27,61,0.18)] overflow-hidden ring-1 ring-black/5" data-testid={`menu-card-${pIdx}`}>
       <button onClick={() => toggleMenu(pIdx)} className="w-full flex items-center justify-between gap-4 px-5 lg:px-7 py-5 text-left hover:bg-gray-50 transition-colors" data-testid={`menu-toggle-${pIdx}`}>
        <div className="flex items-center gap-4 min-w-0">
         <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 font-black text-xs" style={{ background: theme.grad, color: "#fff" }}>{String(pIdx + 1).padStart(2, "0")}</span>
         <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.28em] font-black truncate" style={{ color: theme.main }}>{p.name}, menu</div>
          <div className="font-black text-base lg:text-lg tracking-tight mt-0.5 truncate" style={{ color: INK }}>{p.menuTitle}</div>
         </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
         <span className="hidden sm:inline-block font-black text-base tracking-tight" style={{ color: theme.main }}>{p.price}</span>
         <ChevronDown className={`w-5 h-5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} style={{ color: theme.main }} />
        </div>
       </button>
       {isOpen && (
        <div className="px-5 lg:px-7 pb-7 lg:pb-8 border-t" style={{ borderColor: "rgba(15,27,61,0.06)" }}>
         <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mt-7">
          {Object.entries(p.menu).map(([course, items], idx) => (
           <div key={course} data-testid={`menu-course-${pIdx}-${idx}`}>
            <div className="text-[10px] uppercase tracking-[0.28em] font-black mb-3 flex items-center gap-2" style={{ color: theme.main }}>
             <span className="w-6 h-px" style={{ backgroundColor: theme.main }} />
             {course}
            </div>
            <ul className="space-y-2.5">
             {items.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed" style={{ color: INK }}>
               <span className="block w-1.5 h-1.5 rounded-full mt-2 shrink-0" style={{ backgroundColor: theme.main }} />
               <span>{item}</span>
              </li>
             ))}
            </ul>
           </div>
          ))}
         </div>
         {p.beverage && (
          <div className="mt-7 pt-6 border-t" style={{ borderColor: "rgba(15,27,61,0.06)" }}>
           <div className="grid md:grid-cols-12 gap-5 items-start">
            <div className="md:col-span-4">
             <div className="text-[10px] uppercase tracking-[0.28em] font-black mb-2 flex items-center gap-2" style={{ color: theme.main }}><Wine className="w-3.5 h-3.5" /> Beverage package</div>
             <div className="font-black text-base tracking-tight" style={{ color: INK }}>Premium drinks included</div>
             <div className="text-xs italic mt-1" style={{ color: INK_SOFT }}>Spirits via bar tab or individual purchase</div>
            </div>
            <div className="md:col-span-8 grid sm:grid-cols-2 gap-2.5">
             {p.beverage.map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-sm" style={{ color: INK }}>
               <Check className="w-3.5 h-3.5 shrink-0" style={{ color: theme.main }} strokeWidth={2.8} /> <span>{b}</span>
              </div>
             ))}
            </div>
           </div>
          </div>
         )}
        </div>
       )}
      </div>
     );
    })}
   </div>
  </div>
 </section>
 )}


  {/* ENQUIRY block removed, single combined Enquiry+Availability section now lives at the top of the page (rendered by parent SEOLanding / BYOCruise) */}

  {/* ───── ENTERTAINMENT ADD-ONS ───── */}
  <section className="relative py-14 lg:py-20" style={{ backgroundColor: CREAM }}>
   <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
    <div className="max-w-3xl mb-12">
     <div className="text-[11px] uppercase tracking-[0.32em] font-black mb-3 flex items-center gap-3" style={{ color: theme.main }}>
      <span className="w-8 h-px" style={{ backgroundColor: theme.main }} /> Entertainment add-ons
     </div>
     <h2 className="font-black tracking-tight leading-[0.95]" style={{ fontSize: "clamp(1.85rem, 4vw, 3rem)", letterSpacing: "-0.022em", color: INK }}>
      Make the night unforgettable. <span className="italic font-light" style={{ color: theme.main }}>Add what you need.</span>
     </h2>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
     {entertainment.map((e, i) => {
      const Ico = ICON_MAP[e.Icon] || Star;
      return (
       <div key={i} className="rounded-2xl bg-white p-5 lg:p-6 shadow-[0_15px_35px_-15px_rgba(15,27,61,0.1)] hover:-translate-y-1 hover:shadow-[0_25px_50px_-15px_rgba(15,27,61,0.18)] transition-all" data-testid={`addon-${i}`}>
        <div className="w-10 h-10 rounded-xl inline-flex items-center justify-center mb-3" style={{ backgroundColor: theme.soft }}>
         <Ico className="w-5 h-5" style={{ color: theme.main }} strokeWidth={2} />
        </div>
        <div className="font-black text-sm tracking-tight" style={{ color: INK }}>{e.label}</div>
        <div className="text-xs font-black mt-1" style={{ color: theme.main }}>{e.price}</div>
       </div>
      );
     })}
    </div>
   </div>
  </section>

  {/* Package FAQs + Final Ribbon CTA removed, SEOLanding/page handles FAQ + booking CTA at the bottom (no duplicates) */}

 </div>
 );
}
